import { PromptTemplateConfiguration } from "@/types/evaluate";
import { action, makeObservable, observable } from "mobx";
import React, { createContext, useContext } from "react";

class DialogStore {
  @observable isOpen = false;
  @observable configuration: Partial<PromptTemplateConfiguration> = {};
  @observable prev_configuration: Partial<PromptTemplateConfiguration> = {};
  private onConfigurationChange?: (
    config: Partial<PromptTemplateConfiguration>,
  ) => void;

  constructor() {
    makeObservable(this);
  }

  @action
  openDialog(
    config: Partial<PromptTemplateConfiguration>,
    callback?: (config: Partial<PromptTemplateConfiguration>) => void,
  ) {
    this.prev_configuration = config;
    this.configuration = config;
    this.onConfigurationChange = callback;
    this.isOpen = true;
  }

  @action
  closeDialog() {
    this.isOpen = false;
    this.prev_configuration = {};
    this.configuration = {};
    this.onConfigurationChange = undefined;
  }

  @action
  setOpen(value: boolean) {
    this.isOpen = value;
  }

  @action
  setConfiguration(config: Partial<PromptTemplateConfiguration>) {
    this.configuration = config;
    if (this.onConfigurationChange) {
      this.onConfigurationChange(config);
    }
  }

  @action
  confirm() {
    this.closeDialog();
  }

  @action
  cancel() {
    if (this.onConfigurationChange) {
      this.onConfigurationChange(this.prev_configuration);
    }
    this.closeDialog();
  }
}

const DialogStoreContext = createContext<DialogStore | null>(null);

const DialogStoreProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [store] = React.useState(() => new DialogStore());

  return (
    <DialogStoreContext.Provider value={store}>
      {children}
    </DialogStoreContext.Provider>
  );
};

export const useSnippetDialog = (): DialogStore => {
  const context = useContext(DialogStoreContext);
  if (!context) {
    throw new Error("useDialog must be used within a DialogStoreProvider");
  }
  return context;
};

export const withSnippetDialog = <P extends object>(
  Component: React.ComponentType<P>,
): React.FC<P> => {
  return (props: P) => (
    <DialogStoreProvider>
      <Component {...props} />
    </DialogStoreProvider>
  );
};

export type IDialog = DialogStore;

export default DialogStore;
